import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Pause, Play, Trash2 } from "lucide-react";

import {
    BACKGROUND_TRACKS,
    BACKGROUND_TRACK_GAIN,
} from "../../domain/backgroundTracks";
import {
    useDeleteMeditationTrack,
    useCenterMeditationTracks,
    useMeditationTracks,
    usePublicMeditationTracks,
    trackPlaybackUrl,
} from "../../application/useMeditationTracks";
import { BackgroundTrackId } from "../../domain/types";
import MeditationTrackUploader from "./MeditationTrackUploader";

export type BedChoice = {
    track: BackgroundTrackId;
    /** Set when the bed is one of the therapist's own tracks. */
    trackId: string | null;
};

type Props = {
    value: BackgroundTrackId;
    trackId?: string | null;
    onChange: (next: BedChoice) => void;
    disabled?: boolean;
    /**
     * For a meditation of the centre's library, made by one of its
     * administrators: music uploaded here goes on the centre's shelf.
     */
    forCenter?: boolean;
};

/**
 * Choosing the bed a meditation sits on.
 *
 * Silence, and the therapist's own music. We shipped three beds so that one
 * was a press away; they were not good enough to guide anybody through, and a
 * bed you would not use yourself is worse than none — it is the one thing the
 * patient hears for twenty minutes. A meditation saved with one of them still
 * plays it; nothing new is laid on ours.
 *
 * Music they upload is theirs in the other sense too, which is why the form
 * asks them to say so before it will send the file. We cannot check where a
 * piece of music came from, and a published meditation carries its bed to
 * whoever copies it — so the one honest thing to do is ask the only person who
 * knows.
 *
 * Every option can be listened to before it is chosen, at the level it will be
 * mixed at: picking music from a list of names and finding out what it sounds
 * like with a patient on the line is not a choice, it is a gamble.
 */
export default function BackgroundTrackPicker({
    value,
    trackId = null,
    onChange,
    disabled = false,
    forCenter = false,
}: Props) {
    const { t } = useTranslation();
    const { data: tracks } = useMeditationTracks();
    // The centre's shelf too, for a therapist in one. Nobody but its
    // administrators removes music from it, so it has no delete here.
    const { data: centerTracks } = useCenterMeditationTracks();
    // And the public catalogue: music other therapists chose to share. Their
    // own shared tracks are already listed as theirs above.
    const { data: publicTracks } = usePublicMeditationTracks();
    const sharedByOthers = (publicTracks ?? []).filter((track) => !track.can_manage);
    const deleteTrack = useDeleteMeditationTrack();

    const [previewing, setPreviewing] = useState<string | null>(null);
    const [error, setError] = useState(false);
    const audioRef = useRef<HTMLAudioElement | null>(null);

    const stopPreview = () => {
        audioRef.current?.pause();
        audioRef.current = null;
        setPreviewing(null);
    };

    const preview = (id: string, src: string) => {
        if (previewing === id) {
            stopPreview();
            return;
        }
        audioRef.current?.pause();
        const audio = new Audio(src);
        audio.volume = BACKGROUND_TRACK_GAIN;
        audio.onended = () => setPreviewing(null);
        audioRef.current = audio;
        void audio.play().catch(() => setPreviewing(null));
        setPreviewing(id);
    };

    const previewOwn = async (id: string) => {
        if (previewing === id) {
            stopPreview();
            return;
        }
        // The file is not public, so a preview needs a signed URL first.
        try {
            preview(id, await trackPlaybackUrl(id));
        } catch {
            setError(true);
        }
    };

    // A preview must not outlive the form it was started from.
    useEffect(() => () => audioRef.current?.pause(), []);

    const row = (
        key: string,
        label: string,
        selected: boolean,
        onSelect: () => void,
        hint?: string,
        actions?: React.ReactNode
    ) => (
        <li key={key} className="min-w-0">
            <div
                className={`flex min-w-0 items-center justify-between gap-2 rounded-xl border px-3 py-2 ${
                    selected
                        ? "border-accent bg-accent-tint"
                        : "border-border-subtle bg-surface-raised"
                }`}
            >
                <label className="flex min-h-11 min-w-0 flex-1 cursor-pointer items-center gap-2 text-sm text-content-primary">
                    <input
                        type="radio"
                        name="background-track"
                        checked={selected}
                        onChange={onSelect}
                        className="size-4 accent-[var(--color-accent)]"
                    />
                    <span className="min-w-0">
                        <span className="block truncate">{label}</span>
                        {hint && (
                            <span className="block truncate text-xs text-content-tertiary">
                                {hint}
                            </span>
                        )}
                    </span>
                </label>
                {actions}
            </div>
        </li>
    );

    const previewButton = (id: string, label: string, onClick: () => void) => (
        <button
            type="button"
            onClick={onClick}
            aria-label={t(
                previewing === id ? "meditation.stopPreview" : "meditation.previewTrack",
                { track: label }
            )}
            className="flex size-11 shrink-0 items-center justify-center rounded-lg text-content-secondary hover:bg-surface-sunken"
        >
            {previewing === id ? <Pause size={16} /> : <Play size={16} />}
        </button>
    );

    return (
        <fieldset disabled={disabled} className="min-w-0">
            <legend className="mb-2 text-sm font-medium text-content-secondary">
                {t("meditation.backgroundTrack")}
            </legend>

            {/*
              * `minmax(0,1fr)`: a grid track sizes to its content by default,
              * and the longest credit line was pushing the preview button out
              * of the panel it sits in.
              */}
            <ul className="grid gap-2 sm:grid-cols-[repeat(2,minmax(0,1fr))]">
                {BACKGROUND_TRACKS.map((track) =>
                    row(
                        track.id,
                        t(track.labelKey),
                        value === track.id && !trackId,
                        () => onChange({ track: track.id, trackId: null }),
                        track.creditKey ? t(track.creditKey) : undefined,
                        track.src
                            ? previewButton(track.id, t(track.labelKey), () =>
                                  preview(track.id, track.src as string)
                              )
                            : undefined
                    )
                )}

                {(tracks ?? []).map((track) =>
                    row(
                        track.id,
                        track.name,
                        trackId === track.id,
                        () =>
                            onChange({
                                track: BackgroundTrackId.NONE,
                                trackId: track.id,
                            }),
                        t("meditation.tracks.own"),
                        <span className="flex shrink-0 items-center">
                            {previewButton(track.id, track.name, () =>
                                void previewOwn(track.id)
                            )}
                            <button
                                type="button"
                                onClick={() => {
                                    stopPreview();
                                    if (trackId === track.id) {
                                        onChange({
                                            track: BackgroundTrackId.NONE,
                                            trackId: null,
                                        });
                                    }
                                    void deleteTrack.mutateAsync(track.id);
                                }}
                                aria-label={t("meditation.tracks.remove", {
                                    track: track.name,
                                })}
                                className="flex size-11 shrink-0 items-center justify-center rounded-lg text-content-secondary hover:bg-surface-sunken"
                            >
                                <Trash2 size={15} />
                            </button>
                        </span>
                    )
                )}

                {(centerTracks ?? []).map((track) =>
                    row(
                        track.id,
                        track.name,
                        trackId === track.id,
                        () =>
                            onChange({
                                track: BackgroundTrackId.NONE,
                                trackId: track.id,
                            }),
                        t("meditation.tracks.center"),
                        <span className="flex shrink-0 items-center">
                            {previewButton(track.id, track.name, () =>
                                void previewOwn(track.id)
                            )}
                        </span>
                    )
                )}

                {sharedByOthers.map((track) =>
                    row(
                        track.id,
                        track.name,
                        trackId === track.id,
                        () =>
                            onChange({
                                track: BackgroundTrackId.NONE,
                                trackId: track.id,
                            }),
                        t("meditation.tracks.public"),
                        <span className="flex shrink-0 items-center">
                            {previewButton(track.id, track.name, () =>
                                void previewOwn(track.id)
                            )}
                        </span>
                    )
                )}
            </ul>

            <div className="mt-3">
                <MeditationTrackUploader
                    forCenter={forCenter}
                    onAdded={(track) =>
                        onChange({
                            track: BackgroundTrackId.NONE,
                            trackId: track.id,
                        })
                    }
                />
                {error && (
                    <p role="alert" className="mt-2 text-xs text-status-critical">
                        {t("meditation.tracks.failed")}
                    </p>
                )}
            </div>
        </fieldset>
    );
}
